import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  FormControlLabel,
  Select,
  MenuItem,
  ListItemText,
  OutlinedInput,
  Button,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FileRejection, useDropzone } from 'react-dropzone';
import IconUpload from '../../assets/icons/IconUpload';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { styled } from '@mui/material/styles';
import GalleryActions from './GalleryActions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, useAppSelector } from '../../redux/Store';
import {
  addToUploadQueue,
  QueueFileData,
  updateQueueMediaStatus,
  removeRejectedUploadQueue,
} from '../../redux/reducer/galleryReducer';
import LabelMediaItem from './LabelMediaItem';
import LabelMediaAdditionalItem from './LabelMediaAdditionalItem';
import { v4 as uuidv4 } from 'uuid';
import useMediaLabels from '../MainComponent/hooks/useMediaLabels';
import Notifications from '../../ui/molecules/toast/Notifications';
import QueueMediaComponent from './queue-media';
import useImages from '../MainComponent/hooks/useImages';
import DraggableImage from './DraggableImage';
import ImageSelectBox from './ImageSelectionBox';
import {
  addMediaLabelToImage,
  getScriptState,
} from '../../redux/reducer/coreScriptReducer';
import { Media } from '@safeguardproperties/corescript-react-npm/dist/types';
import CustomCheckbox from '../../ui/atoms/checkbox';
import IconCheckHalf from '../../assets/icons/IconCheckHalf';
import ImageDialog from './ImageDialog';
import useMediaHelper from '../MainComponent/hooks/useMediaHelper';
import {
  downloadMultipleFilesAsZip,
  debounce,
  sameFileExtension,
} from '../../utils';
import { getImageDimensions2 } from '../../utils/helpers/filehelper';
import dayjs from 'dayjs';
import { contextValue, DateFormat } from '../../constants';

const CustomSelect = styled(Select)(({ theme }) => ({
  color: '#1D6EC9',
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:before': {
    borderBottom: 'none',
  },
  '&:after': {
    borderBottom: 'none',
  },
  '& .MuiSelect-select': {
    padding: '4px 0px',
  },
}));

type TabType = 'all' | 'fulfilled' | 'unfulfilled' | 'additional';

const getFileMimeType = async (file: File): Promise<string | null> => {
  const arrayBuffer = await file.slice(0, 4).arrayBuffer(); // Read first 4 bytes
  const uint8Array = new Uint8Array(arrayBuffer);

  //Bytes 1–2 (0xFF 0xD8): This is the Start of Image (SOI) marker—every JPEG file begins with this.
  //Bytes 3–4: These usually start with 0xFF followed by a marker indicating the segment type.
  //fourth byte (technically part of that segment marker) helps define what kind of metadata or structure the JPEG includes.
  //It’s important if you want to differentiate between JPEGs that came from cameras, phones, or editing software, since EXIF data often includes GPS, orientation, or camera settings.
  const isJPEG =
    uint8Array[0] === 0xff && uint8Array[1] === 0xd8 && uint8Array[2] === 0xff;
  if (isJPEG) {
    return 'image/jpeg';
  }

  const header = uint8Array.reduce(
    (acc, byte) => acc + byte.toString(16).padStart(2, '0'),
    ''
  );
  let mimeType: string | null = null;

  // Check magic numbers (hex signatures) for known formats
  switch (header) {
    case '89504e47': {
      // PNG
      mimeType = 'image/png';
      break;
    }
    case 'ffd8ffe0': // JPG/JPEG
    case 'ffd8ffe1':
    case 'ffd8ffe2':
    case 'ffd8ffe3':
    case 'ffd8ffe8': {
      mimeType = 'image/jpeg';
      break;
    }
    default: {
      mimeType = null;
    } // Unknown format
  }

  return mimeType;
};

const GallerySidePanel = ({
  open,
  onClose,
  onExpand,
  expanded,
  uploadedFiles,
  allowAddtionalImageLabels,
}: any) => {
  const contextId = useAppSelector((state) => state.coreScript.contextId);

  const { getAllLabeledImages } = useMediaHelper();

  const dispatch = useDispatch();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollDirection, setScrollDirection] = useState<null | 'up' | 'down'>(
    null
  );

  const scrollIntervalRef = useRef<number | null>(null);
  const autoScrollThreshold = expanded ? 700 : 200; // Threshold in pixels for auto-scroll trigger
  const [selectedTab, setSelectedTab] = useState<TabType>('all');
  const { mediaLabels, additionalPhotoLabels, count } =
    useMediaLabels(selectedTab);
  const isReadOnly = useSelector(
    (state: RootState) => state.contextId.isReadOnly
  );
  const queryObj = useSelector((state: RootState) => state.contextId.queryObj);
  const { uploadQueue, cloneQueue, uploadedImagesImageTypePhotos } =
    useAppSelector((state) => state.gallery);

  const isAuditBySubmissionId = useMemo(() => {
    return queryObj?.context === contextValue?.AuditResultsBySubmissionId;
  }, [queryObj?.context]);

  //Script state media labels
  const sortedMediaLabels = useMemo(() => {
    return expanded
      ? [...mediaLabels].sort((a: any, b: any) => {
          // Place items where a.Have < a.Min first
          const conditionA = a?.Have < a?.Min ? -1 : 1;
          const conditionB = b?.Have < b?.Min ? -1 : 1;
          if (conditionA !== conditionB) return conditionA - conditionB;
          return a.Label.toLanguagePack().localeCompare(
            b.Label.toLanguagePack()
          );
        })
      : mediaLabels;
  }, [expanded, mediaLabels]);

  // Labels used to update description in image viewer  will not be associated with a question
  const sortedadditionalPhotoLabels = useMemo(() => {
    return [...additionalPhotoLabels].sort((a: any, b: any) => {
      // Place items where a.Have < a.Min first
      const aHasImages =
        Array.isArray(a.Images) && a.Images.length > 0 ? -1 : 1;
      const bHasImages =
        Array.isArray(b.Images) && b.Images.length > 0 ? -1 : 1;
      if (aHasImages !== bHasImages) return aHasImages - bHasImages;
      return a.Label.toLanguagePack().localeCompare(b.Label.toLanguagePack());
    });
  }, [additionalPhotoLabels]);

  const [additionalLabelFiltered, setAdditionalLabelFilter] = useState(
    sortedadditionalPhotoLabels
  );
  useEffect(() => {
    if (selectedTab === 'additional') {
      setAdditionalLabelFilter(sortedadditionalPhotoLabels);
    }
  }, [sortedadditionalPhotoLabels, selectedTab]);

  const handleFilterChangeAddtional = debounce((targetLabel: string) => {
    if (!targetLabel.trim())
      setAdditionalLabelFilter(sortedadditionalPhotoLabels);
    const filter = targetLabel.trim().toLowerCase();
    setAdditionalLabelFilter(
      sortedadditionalPhotoLabels.filter((item: any) =>
        (item?.Label || '').toLanguagePack().toLowerCase().includes(filter)
      )
    );
  }, 50);

  const { labels: activeLabels } = useMediaLabels();

  const [selectedGuids, setSelectedGuids] = useState<string[]>([]);

  const { getUnlabeledImages } = useMediaHelper();
  const unlabeledImages = useMemo(() => {
    const result = getUnlabeledImages();
    return result;
  }, [getUnlabeledImages]);

  const uploadedOrCreatedImagesOrRejected = useMemo(() => {
    return uploadQueue?.length > 0
      ? uploadQueue.some(
          (item) =>
            item.status === 'uploaded' ||
            item.status === 'created' ||
            item.status === 'rejected'
        )
      : false;
  }, [uploadQueue]);

  const { fetchImages } = useImages();
  const onRefreshImageStatus = async () => {
    if (uploadedOrCreatedImagesOrRejected) {
      const response = await fetchImages();
      if (response.images) {
        //checks images in image detail table again requested uploads
        //guid in image detail table is the correlation id
        const guidsthatWereUploaded = uploadQueue
          .filter(
            (item) =>
              (item.status === 'uploaded' || item.status === 'created') &&
              Object.prototype.hasOwnProperty.call(response.images, item.guid)
          )
          .map((item) => item.guid);

        if (guidsthatWereUploaded.length > 0) {
          for (const guid of guidsthatWereUploaded) {
            dispatch(
              updateQueueMediaStatus({ guid, status: 'success', reasons: [] })
            );
          }
        }

        dispatch(removeRejectedUploadQueue());
      }
    }
  };

  const isAllMediaSelected = useMemo(() => {
    if (unlabeledImages.length === 0) return false;
    return unlabeledImages.every((media) => selectedGuids.includes(media.guid));
  }, [unlabeledImages, selectedGuids]);

  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);

  const onDropSelectedFiles = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    // Handle file rejections
    for (const { file, errors } of fileRejections) {
      for (const error of errors) {
        if (error.code === 'file-too-large') {
          Notifications(`${file.name} is too large! Max size is 20MB.`, 0);
        } else if (error.code === 'file-invalid-type') {
          Notifications(
            `${file.name} is not a valid file type. Accepted type is .jpg.`,
            0
          );
        }
      }
    }

    const validFiles: File[] = [];

    for (const file of acceptedFiles) {
      const actualMimeType = await getFileMimeType(file);

      // if (actualMimeType === 'image/png') {
      // Notifications(`${file.name} is not allowed. PNG files are not accepted.`, 0);
      //continue;
      //}

      if (actualMimeType !== 'image/jpeg') {
        Notifications(`${file.name} is not a valid jpg file.`, 0);
        continue;
      }

      try {
        const dimensions = await getImageDimensions2(file);
        validFiles.push(file);
        console.log('Dimensions:', dimensions);
      } catch (error) {
        console.error('Failed to get image dimensions:', error);
      }
    }

    // Maintain existing unique file logic
    const uniqueFiles = validFiles.filter(
      (file) =>
        !uploadedFiles.some(
          (uploadedFile: any) => uploadedFile.name === file.name
        )
    );

    const mediaFiles: QueueFileData[] = uniqueFiles.map((file) => ({
      guid: uuidv4(),
      status: 'pending',
      file,
      reasons: [],
    }));

    if (mediaFiles.length > 0) {
      dispatch(addToUploadQueue(mediaFiles));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      // 'application/pdf': ['.pdf'],
      // 'video/mp4': ['.mp4'],
    },
    maxSize: 20 * 1024 * 1024,
    onDrop: onDropSelectedFiles,
  });

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      stopAutoScroll();
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setSelectedGuids([]);
      setSelectedLabels([]);
    }
  }, [open]);
  /*
  useEffect(() => {
    if (shouldFetchImages) {
      fetchImages();
    }
  }, [shouldFetchImages, fetchImages, dispatch]);
  */

  if (!open) return null;

  const handleChange = (_: any, value: string) => {
    setSelectedTab(value as TabType);
  };

  const onSelectUnselectLabel = (event: any) => {
    const labels = event.target.value as string[];
    setSelectedLabels(labels);
  };

  const onBlurMoveLabelSelection = () => {
    // Ensure selectedLabels and selectedMedia are valid
    if (selectedLabels.length === 0 || selectedGuids.length === 0) {
      console.warn('No labels or media selected.');
      return;
    }

    const media: Media[] = [];
    for (const guid of selectedGuids) {
      const item = unlabeledImages.find((file) => file.guid === guid);
      if (item) {
        media.push({
          Guid: item.guid,
          ThumbUri: item.webFileName,
        });
      }
    }

    for (const label of selectedLabels) {
      dispatch(
        addMediaLabelToImage({
          label: label,
          media: media,
        })
      );
    }

    setSelectedGuids([]);
    setSelectedLabels([]);
  };

  const onSelectMediaFile = (guid: string) => {
    const array = [...selectedGuids];
    const itemIndex = array.indexOf(guid);
    if (itemIndex === -1) {
      array.push(guid);
    } else {
      array.splice(itemIndex, 1);
    }
    setSelectedGuids(array);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();

    if (!scrollRef.current) return;

    const { clientY } = e;
    const { top, bottom } = scrollRef.current.getBoundingClientRect();

    if (clientY - top < autoScrollThreshold) {
      // Near the top edge
      if (scrollDirection !== 'up') {
        setScrollDirection('up');
        stopAutoScroll();
        startAutoScroll('up');
      }
    } else if (bottom - clientY < autoScrollThreshold) {
      // Near the bottom edge
      if (scrollDirection !== 'down') {
        setScrollDirection('down');
        stopAutoScroll();
        startAutoScroll('down');
      }
    } else {
      // Not near any edge
      setScrollDirection(null);
      stopAutoScroll();
    }
  };

  const startAutoScroll = (direction: 'up' | 'down') => {
    if (scrollIntervalRef.current) return; // Prevent multiple intervals

    scrollIntervalRef.current = globalThis.setInterval(() => {
      if (scrollRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

        if (direction === 'up') {
          scrollRef.current.scrollTop = Math.max(0, scrollTop - 10); // Scroll up
        } else if (direction === 'down') {
          scrollRef.current.scrollTop = Math.min(
            scrollHeight - clientHeight,
            scrollTop + 10
          ); // Scroll down
        }
      }
    }, 16) as unknown as number; // Cast to number
  };

  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const handleDragLeave = () => {
    setScrollDirection(null);
    stopAutoScroll();
  };

  const handleDragEnd = () => {
    setScrollDirection(null);
    stopAutoScroll();
  };

  const onDownloadAsZip = () => {
    const filesToDownload = isAuditBySubmissionId
      ? getAllLabeledImages()
      : uploadedImagesImageTypePhotos;
    const files = filesToDownload.map((e) => {
      return {
        url: e.webFileName,
        fileName: sameFileExtension(e.webFileName, e.guid),
      };
    });
    const value = dayjs()
      ?.format(DateFormat.DATE_TIME_YYYY_MM_DD_HH_MM_a)
      .replaceAll(' ', '_');
    downloadMultipleFilesAsZip(files, `${value}-${contextId}.zip`);
  };

  return (
    <Box sx={{ height: '100%', width: expanded ? '100%' : undefined }}>
      <GalleryActions
        onClose={onClose}
        onExpand={onExpand}
        onZipDownload={onDownloadAsZip}
      />

      <Box
        ref={scrollRef} // The main scroll container
        sx={{
          border: '1px solid #EDEDED',
          width: '100%',
          flex: 1,
          maxWidth: expanded ? '100%' : '376px',
          gap: '16px',
          borderRadius: 3,
          flexDirection: 'column',
          transition: 'width 0.3s, transform 0.3s',
          padding: '16px',
          // scrollbarWidth: 'none', // ForFirefox
          // '&::-webkit-scrollbar': {
          //   display: 'none', // ForChrome, Safari, andEdge
          // },
          backgroundColor: '#fbfbfb',
          overflowY: expanded ? 'auto' : 'auto', // Overflow hidden when expanded
          height: expanded ? '100%' : undefined,
          maxHeight: expanded ? undefined : '68vh',
        }}
        onDragOver={handleDragOver} // Add drag over event
        onDragLeave={handleDragLeave} // Stop scrolling when leaving
        onDragEnd={handleDragEnd} // Stop scrolling when drag ends
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: `${expanded ? 'row' : 'column'}`,
            justifyContent: 'space-between',
            gap: '10px',
            height: expanded ? '100%' : undefined,
            overflowY: expanded ? 'auto' : undefined, // Add scroll for content overflow
          }}
        >
          {/* Left Box */}
          {
            <Box
              sx={{
                flex: expanded ? undefined : 1,
                width: expanded ? '50%' : '100%',
                // scrollbarWidth: 'none', // ForFirefox
                // '&::-webkit-scrollbar': {
                //   display: 'none', // ForChrome, Safari, andEdge
                // },
                overflowY: expanded ? 'auto' : 'hidden', // Add scroll for content overflow
                height: expanded ? '100vh' : undefined,
                //maxHeight: expanded ? '65vh' : undefined, // Limit height when expanded
                paddingRight: expanded ? 2 : 0, // Add space for scrollbar
                paddingBottom: expanded ? 2 : 0,
              }}
            >
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed #e3e3e3',
                  borderRadius: 2,
                  padding: 1,
                  textAlign: 'center',
                  backgroundColor: 'white',
                  cursor: isReadOnly ? 'not-allowed' : 'pointer',
                  mb: 1,
                  display: isReadOnly ? 'none' : 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '4px',
                  alignSelf: 'stretch',
                }}
              >
                <IconUpload />
                <input disabled={isReadOnly} {...getInputProps()} />
                {isDragActive ? (
                  <Typography>Drop the files here...</Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '4px',
                      alignSelf: 'stretch',
                      mt: '2px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '11px',
                        fontWeight: 600,
                        lineHeight: '18px',
                        textAlign: 'center',
                        textDecorationStyle: 'solid',
                        textUnderlinePosition: 'from-font',
                        textDecorationSkipInk: 'none',
                      }}
                    >
                      <strong
                        style={{
                          color: '#1D6EC9',
                          textDecorationLine: 'underline',
                        }}
                      >
                        Click to upload{' '}
                      </strong>
                      or drag & drop
                    </Typography>
                    <Typography
                      sx={{
                        color: '#646464',
                        textAlign: 'center',
                        fontFamily: 'Montserrat',
                        fontSize: '8px',
                        fontStyle: 'normal',
                        fontWeight: '400',
                        lineHeight: 'normal',
                        // textTransform: 'capitalize',
                        mt: '2px',
                      }}
                    >
                      Max file size: 20 MB File accepted: jpg
                    </Typography>
                  </Box>
                )}
              </Box>

              <Accordion
                elevation={0}
                defaultExpanded
                sx={{
                  padding: '0px 0px 0px 0px',
                  backgroundColor: 'transparent',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls='uploaded-files-content'
                  id='uploaded-files-header'
                  sx={{
                    padding: '0px 0px 0px 0px',
                    mt: '-15px',
                  }}
                >
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                    width='100%'
                  >
                    <Typography
                      sx={{
                        color: '#343330',
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: 'normal',
                        textTransform: 'capitalize',
                      }}
                    >
                      Uploaded Images{' '}
                      {unlabeledImages.length > 0
                        ? `(${unlabeledImages.length})`
                        : ''}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: '0px 0px 0px 0px',
                    mt: '-15px',
                  }}
                >
                  <Typography
                    variant='body1'
                    sx={{
                      color: '#646464',
                      fontFamily: 'Montserrat',
                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      lineHeight: 'normal',
                      textTransform: 'capitalize',
                      display: isReadOnly ? 'none' : 'flex',
                    }}
                  >
                    Select or drag images under labels
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                    gap={2}
                  >
                    <FormControlLabel
                      control={
                        <CustomCheckbox
                          sx={{ ml: '5px' }}
                          icon={
                            !isAllMediaSelected && selectedGuids.length > 0 ? (
                              <IconCheckHalf />
                            ) : undefined
                          }
                          onChange={() =>
                            setSelectedGuids(
                              isAllMediaSelected
                                ? []
                                : unlabeledImages.map((item) => item.guid)
                            )
                          }
                          checked={isAllMediaSelected}
                        />
                      }
                      label='Select All'
                      sx={{
                        color: '#343330',
                        fontFamily: 'Montserrat',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: '14.63px',
                        textTransform: 'capitalize',
                        my: 2,
                        textAlign: 'left',
                        textDecoration: 'none',
                        textUnderlinePosition: 'from-font',
                        textDecorationSkipInk: 'none',
                      }}
                    />
                    {uploadedOrCreatedImagesOrRejected && (
                      <Button
                        onClick={onRefreshImageStatus}
                        variant='text'
                        size='small'
                        sx={{
                          minWidth: 'auto',
                          padding: '4px 8px',
                          fontSize: '0.875rem',
                          textTransform: 'none',
                          lineHeight: 'normal',
                        }}
                      >
                        Refresh
                      </Button>
                    )}
                    {selectedGuids.length > 0 && (
                      <CustomSelect
                        variant='standard'
                        multiple
                        value={selectedLabels}
                        onChange={onSelectUnselectLabel}
                        displayEmpty
                        input={<OutlinedInput />}
                        onClose={() =>
                          setTimeout(onBlurMoveLabelSelection, 500)
                        }
                        renderValue={(selected: any) =>
                          selected.length === 0 ? (
                            <Typography
                              sx={{
                                color: '#1D6EC9',
                                fontFamily: 'Montserrat',
                                fontSize: '12px',
                                fontStyle: 'normal',
                                fontWeight: '500',
                                lineHeight: 'normal',
                                textTransform: 'capitalize',
                              }}
                            >
                              Move to Label
                            </Typography>
                          ) : (
                            `${selected.length} Labels`
                          )
                        }
                        IconComponent={(props) => (
                          <ArrowDropDownIcon
                            {...props}
                            sx={{ color: '#1D6EC9' }}
                          />
                        )}
                        sx={{
                          color: '#1D6EC9',
                          fontFamily: 'Montserrat',
                          fontSize: '12px',
                          fontStyle: 'normal',
                          fontWeight: '500',
                          lineHeight: 'normal',
                          textTransform: 'capitalize',
                          border: 'none',
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              marginTop: 0,
                            },
                          },
                          autoFocus: false,
                          disableAutoFocusItem: true,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <Button
                            onClick={onBlurMoveLabelSelection}
                            variant='text'
                            size='small'
                            sx={{
                              minWidth: 'auto', // Prevent unnecessary width
                              padding: '4px 8px',
                              fontSize: '0.875rem',
                              textTransform: 'none', // Maintain natural casing
                              lineHeight: 'normal',
                            }}
                          >
                            CLOSE
                          </Button>
                        </Box>
                        {activeLabels.map((labelName: string, index: any) => (
                          <MenuItem key={index} value={labelName}>
                            <CustomCheckbox
                              checked={selectedLabels.includes(labelName)}
                            />
                            <ListItemText
                              primary={labelName?.toLanguagePack()}
                            />
                          </MenuItem>
                        ))}
                      </CustomSelect>
                    )}
                  </Box>

                  <ImageSelectBox
                    expanded={expanded}
                    uploadQueue={uploadQueue}
                    unlabeledImages={unlabeledImages}
                    selectedGuids={selectedGuids}
                    onSelectMediaFile={onSelectMediaFile}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          }

          {/* Right Box */}
          <Box
            sx={{
              flex: 1,

              overflowY: expanded ? 'auto' : 'hidden', // Add scroll for content overflow
              height: expanded ? '65vh' : 'none', // Limit height when expanded
              borderLeft: expanded ? '1px solid #E3E3E3' : 'none',
              paddingLeft: expanded ? 2 : 0,
              marginX: expanded ? '16px' : '0',
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleChange}
              aria-label='label tabs'
              variant='scrollable'
              scrollButtons='auto'
              sx={{ minHeight: '48px' }}
            >
              <Tab
                label={`All Labels (${count.all})`}
                value={'all'}
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '14.63px',
                  textAlign: 'left',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                  padding: '0 6px',
                }}
              />
              <Tab
                label={`Unfulfilled Labels (${count.unfulfilled})`}
                value={'unfulfilled'}
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '14.63px',
                  textAlign: 'left',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                  padding: '0 6px',
                }}
              />
              <Tab
                label={`Fulfilled Labels (${count.fulfilled})`}
                value={'fulfilled'}
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '14.63px',
                  textAlign: 'left',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                  padding: '0 6px',
                }}
              />
              {allowAddtionalImageLabels && (
                <Tab
                  label={`Additional (${count.additional})`}
                  value={'additional'}
                  sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '14.63px',
                    textAlign: 'left',
                    textUnderlinePosition: 'from-font',
                    textDecorationSkipInk: 'none',
                    padding: '0 6px',
                  }}
                />
              )}
            </Tabs>

            {selectedTab === 'additional' && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  size='small'
                  variant='outlined'
                  placeholder='Filter by label...'
                  onChange={(e) => handleFilterChangeAddtional(e.target.value)}
                  fullWidth
                />
              </Box>
            )}
            <Grid container spacing={2} sx={{ marginY: 0 }}>
              {selectedTab === 'additional'
                ? additionalLabelFiltered.map(
                    (additional: any, index: number) => (
                      <Grid item xs={expanded ? undefined : 4} key={index}>
                        <LabelMediaAdditionalItem
                          index={index}
                          item={additional}
                          expanded={expanded}
                        />
                      </Grid>
                    )
                  )
                : sortedMediaLabels?.map((media: any, index: number) => (
                    <Grid item xs={expanded ? undefined : 4} key={index}>
                      <LabelMediaItem
                        index={index}
                        item={media}
                        expanded={expanded}
                      />
                    </Grid>
                  ))}
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default GallerySidePanel;
